import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { usePlayerAuth } from "./PlayerAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ProgressContext = createContext(null);

export const ProgressProvider = ({ children }) => {
  const { player, loading: authLoading } = usePlayerAuth();
  const userId = player?.id || null;
  const storageKey = userId ? `noSoccerProgress_${userId}` : "noSoccerProgress_guest";

  const [completed, setCompleted] = useState([]);
  const [journal, setJournal] = useState({});
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load progress for the current identity. For a logged-in player this binds to their
  // real account id and the server is the source of truth; guests/coach-preview stay local-only.
  useEffect(() => {
    if (authLoading) return;
    setLoaded(false);
    const local = JSON.parse(localStorage.getItem(storageKey) || "{}");
    setCompleted(local.completed || []);
    setJournal(local.journal || {});
    setEntries(local.entries || []);
    if (!userId) { setLoaded(true); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/progress/${userId}`);
        if (data) {
          setCompleted(data.completed_chapters || []);
          setJournal(data.journal || {});
          setEntries(data.journal_entries || []);
        }
      } catch (e) {
        // offline / server error — keep local cache
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authLoading]);

  const persist = useCallback((payload) => {
    const snapshot = { completed, journal, entries, ...payload };
    localStorage.setItem(storageKey, JSON.stringify(snapshot));
    // Only sync to the server for a logged-in player — never write guest progress to an account.
    if (!userId) return;
    axios.post(`${API}/progress`, {
      user_id: userId,
      completed_chapters: snapshot.completed,
      journal: snapshot.journal,
      journal_entries: snapshot.entries,
    }).catch(() => {/* sync failure is non-fatal — local cache persists */});
  }, [userId, storageKey, completed, journal, entries]);

  const toggleChapter = useCallback((chapterId) => {
    setCompleted((prev) => {
      const next = prev.includes(chapterId) ? prev.filter((c) => c !== chapterId) : [...prev, chapterId];
      persist({ completed: next });
      return next;
    });
  }, [persist]);

  const saveJournal = useCallback((key, value) => {
    setJournal((prev) => {
      const next = { ...prev, [key]: value };
      persist({ journal: next });
      return next;
    });
  }, [persist]);

  const saveEntries = useCallback((nextEntries) => {
    setEntries(nextEntries);
    persist({ entries: nextEntries });
  }, [persist]);

  return (
    <ProgressContext.Provider value={{ userId, completed, journal, entries, loaded, toggleChapter, saveJournal, saveEntries }}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => useContext(ProgressContext);
